import { useId, type ReactNode } from 'react';

import { Typography } from '@scaleaq/scaleui';

import { Loader } from '@/components/Loader.tsx';
import { Icon } from '@/utils';

import {
  AccordionChevronWrap,
  AccordionHeader,
  AccordionRoot,
  AccordionRule,
} from './categoryAccordion.styles.ts';

type CategoryAccordionProps = {
  title: ReactNode;
  /** Shown as `(count)` after the title. */
  count: number;
  /** Optional content after the count in the header (e.g. compact `<StatusLevelBreakdown />`). */
  trailingHeaderContent?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children?: ReactNode;
  isLoading?: boolean;
};

export const CategoryAccordion = ({
  title,
  count,
  trailingHeaderContent,
  expanded,
  onToggle,
  children,
  isLoading,
}: CategoryAccordionProps) => {
  const headerId = useId();
  const panelId = useId();

  return (
    <AccordionRoot>
      {isLoading && <Loader variant='bounce' />}
      <AccordionHeader
        type='button'
        id={headerId}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <AccordionChevronWrap $expanded={expanded} aria-hidden>
          <Icon.ChevronRight />
        </AccordionChevronWrap>
        <Typography type='heading_xsmall' weight={500}>
          {title}
        </Typography>
        <Typography type='body_medium' secondary weight={500}>
          ({count})
        </Typography>

        {trailingHeaderContent}
        <AccordionRule />
      </AccordionHeader>

      {expanded && (
        <div id={panelId} role='region' aria-labelledby={headerId}>
          {children}
        </div>
      )}
    </AccordionRoot>
  );
};
